import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogPosts';

interface YouMightAlsoLikeProps {
  currentSlug: string;
  currentCategory?: string;
  currentTags?: string[];
}

export default function YouMightAlsoLike({ currentSlug, currentCategory, currentTags = [] }: YouMightAlsoLikeProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Score posts by relevance: same category = 2pts, shared tag = 1pt each
  const scored = blogPosts
    .filter(p => p.slug !== currentSlug)
    .map(p => {
      let score = 0;
      if (p.category === currentCategory) score += 2;
      if (p.tags) {
        p.tags.forEach(tag => {
          if (currentTags.includes(tag)) score += 1;
        });
      }
      return { ...p, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  if (scored.length === 0) return null;

  const categoryColors: Record<string, { bg: string; text: string }> = {
    'Car Buying Tips':    { bg: 'bg-amber-100',   text: 'text-amber-700' },
    'Market Trends':      { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    'Oregon Coast Living':{ bg: 'bg-sky-100',     text: 'text-sky-700' },
    'Recalls & Safety':   { bg: 'bg-red-100',     text: 'text-red-700' },
    'EV & Innovation':    { bg: 'bg-violet-100',  text: 'text-violet-700' },
    'City Broker Guide':  { bg: 'bg-orange-100',  text: 'text-orange-700' },
  };

  return (
    <section className="py-14 bg-white border-t border-[#E8E2D6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Header row */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold tracking-widest text-[#E8B84B] uppercase mb-1">
              Keep Reading
            </p>
            <h2 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#1C2B1E]">
              You Might Also Like
            </h2>
          </div>

          {/* Scroll arrows — desktop */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
              className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer
                ${canScrollLeft
                  ? 'border-[#1C2B1E] text-[#1C2B1E] hover:bg-[#1C2B1E] hover:text-white'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll right"
              className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer
                ${canScrollRight
                  ? 'border-[#1C2B1E] text-[#1C2B1E] hover:bg-[#1C2B1E] hover:text-white'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Scrollable strip */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {scored.map((post) => {
            const catStyle = categoryColors[post.category] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
            const isNew = (() => {
              const d = new Date(post.publishedAt ?? post.date ?? '');
              return !isNaN(d.getTime()) && (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
            })();

            return (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="flex-none w-72 sm:w-80 bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden
                           hover:border-[#E8B84B] hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                {/* Image */}
                <div className="relative w-full h-44 overflow-hidden bg-[#1C2B1E]">
                  <img
                    src={post.heroImage ?? post.image}
                    alt={post.title}
                    width={320}
                    height={176}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                  {/* Category pill */}
                  <span className={`absolute top-3 left-3 px-2.5 py-0.5 text-xs font-semibold rounded-full ${catStyle.bg} ${catStyle.text}`}>
                    {post.category}
                  </span>
                  {/* New badge */}
                  {isNew && (
                    <span className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>
                      New
                    </span>
                  )}
                  {/* Gradient overlay at bottom */}
                  <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-['Oswald'] text-lg font-bold text-[#1C2B1E] leading-snug mb-2
                                 group-hover:text-[#E8B84B] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#E8B84B]
                                     group-hover:gap-2 transition-all">
                      Read
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Mobile scroll hint dots */}
        <div className="flex justify-center gap-1.5 mt-5 sm:hidden">
          {scored.map((_, i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#E8E2D6]"
            />
          ))}
        </div>

        {/* View all link */}
        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-[#1C2B1E] text-[#1C2B1E] text-sm font-semibold
                       rounded-full hover:bg-[#1C2B1E] hover:text-white transition-all whitespace-nowrap cursor-pointer"
          >
            Browse All Articles
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
